import { useEffect } from "react";

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || navigator.maxTouchPoints > 0);

export function useViewSwitch() {
  useEffect(() => {
    console.log('useViewSwitch hook starting...');
    
    // Helper to initialize once all elements are ready
    const initialize = () => {
      console.log('useViewSwitch initialize() called');
      const flowEl = document.querySelector("[js-flow]");
      const gridEl = document.querySelector("[js-grid]");
      const switchButtons = document.querySelectorAll(
        "[js-flow-switch] .g-switch__btn",
      );
      const speedInput = document.querySelector("[js-flow-speed]");
      const darkToggle = document.querySelector("[js-dark-toggle]");

      console.log('Elements found:', { flowEl: !!flowEl, gridEl: !!gridEl, switchButtons: switchButtons.length });

      // We need flow/grid elements for view switching, but not for filter toggle
      const hasFlowGrid = flowEl && gridEl && switchButtons.length >= 2;
      
      if (!hasFlowGrid && !darkToggle) {
        console.log('No elements found, returning null');
        return null; // Nothing to initialize
      }

      const isTouch = isTouchDevice();
      let view = window.hpview || "flow";
      let speed = window.hpspeed || 1;
      let isDark = !!window.hpdark;

      const applyView = () => {
        flowEl.classList.toggle("-active", view === "flow");
        gridEl.classList.toggle("-active", view === "grid");
        if (view === "flow") flowEl.style.opacity = "1";
        switchButtons.forEach((btn, index) => {
          btn.classList.toggle(
            "-active",
            (index === 0 && view === "flow") || (index === 1 && view === "grid"),
          );
        });
        const speedWrapper = speedInput && speedInput.closest(".g-speed");
        if (speedWrapper)
          speedWrapper.classList.toggle("-hidden", view === "grid");
        window.hpview = view;
        window.dispatchEvent(
          new CustomEvent("hpviewchange", { detail: { view } }),
        );
      };

      const syncDarkToggle = () => {
        if (!darkToggle) return;
        const guframBrowsers = document.querySelectorAll(".gufram-browser");
        const anyDarkMode = Array.from(guframBrowsers).some(el => 
          el.classList.contains("-darkmode")
        );
        darkToggle.classList.toggle("-active", anyDarkMode);
      };

      const toggleDark = (event) => {
        event.preventDefault();
        const guframBrowsers = document.querySelectorAll(".gufram-browser");
        if (!guframBrowsers.length) return;
        isDark = !isDark;
        window.hpdark = isDark;
        
        // Apply dark mode to ALL .gufram-browser elements (including sidebar's parent)
        guframBrowsers.forEach(browser => {
          browser.classList.toggle("-darkmode", isDark);
        });
        
        // Also toggle data-theme on html element for page background
        document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
        
        // Also set body background to match
        document.body.style.backgroundColor = isDark ? '#111' : '#fff';
        
        syncDarkToggle();
      };

      const handleSwitchClick = (index) => (event) => {
        event.preventDefault();
        if (document.body.classList.contains("-transition")) return;
        if (index === 0 && view === "flow") {
          window.dispatchEvent(new CustomEvent("hpshuffle"));
          return;
        }
        view = index === 0 ? "flow" : "grid";
        applyView();
      };

      const handlers = [];
      
      // Only set up flow/grid switching if elements exist
      if (hasFlowGrid) {
        switchButtons.forEach((btn, index) => {
          const handler = handleSwitchClick(index);
          handlers.push(handler);
          btn.addEventListener("click", handler);
        });
        
        applyView();
      }

      if (speedInput) {
        speedInput.value = String(speed);
        speedInput.addEventListener("input", (event) => {
          const value = parseFloat(event.target.value);
          speed = Number.isFinite(value) && value > 0 ? value : 1;
          window.hpspeed = speed;
        });
      }

      if (!isTouch) document.body.classList.add("no-touch");
      else document.body.classList.add("is-touch");

      // Apply initial dark mode to all gufram-browser elements
      const guframBrowsers = document.querySelectorAll(".gufram-browser");
      if (isDark) {
        guframBrowsers.forEach(browser => {
          browser.classList.add("-darkmode");
        });
        document.documentElement.setAttribute('data-theme', 'dark');
        document.body.style.backgroundColor = '#111';
      }
      syncDarkToggle();

      if (darkToggle) darkToggle.addEventListener("click", toggleDark);
      

      return () => {
        switchButtons.forEach((btn, index) => {
          const handler = handlers[index];
          if (handler) btn.removeEventListener("click", handler);
        });
        if (darkToggle) darkToggle.removeEventListener("click", toggleDark);
      };
    };

    // Try to initialize immediately
    let cleanup = initialize();
    
    // If initialization failed (elements not ready yet), set up observer
    if (!cleanup) {
      const observer = new MutationObserver(() => {
        cleanup = initialize();
        if (cleanup) {
          // Successfully initialized, disconnect observer
          observer.disconnect();
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      
      // Cleanup function for the observer
      return () => {
        observer.disconnect();
        if (cleanup) cleanup();
      };
    }
    
    return cleanup;
  }, []);
}
